import { LoyaltyServiceOfferStoreRestrictionMode } from 'generated/graphql-gateway';

export type OfferStoreRestrictionMode =
  | LoyaltyServiceOfferStoreRestrictionMode
  | 'ALL_STORES'
  | 'INCLUSIVE'
  | 'EXCLUSIVE'
  | null
  | undefined;

/**
 * Whether a store may be used for an offer given BE restriction mode + id list.
 *
 * - ALL_STORES → any store id is allowed
 * - INCLUSIVE → storeId must be in availableStoreIds (empty list ⇒ nowhere)
 * - EXCLUSIVE → storeId must not be in availableStoreIds
 */
export function isStoreAllowed(
  storeId: string | null | undefined,
  mode: OfferStoreRestrictionMode,
  availableStoreIds: readonly (string | null | undefined)[] | null | undefined
): boolean {
  if (!storeId) {
    return false;
  }

  const ids = (availableStoreIds ?? []).filter((id): id is string => Boolean(id));
  const resolvedMode = mode ?? LoyaltyServiceOfferStoreRestrictionMode.ALL_STORES;

  switch (resolvedMode) {
    case LoyaltyServiceOfferStoreRestrictionMode.INCLUSIVE:
      return ids.includes(storeId);
    case LoyaltyServiceOfferStoreRestrictionMode.EXCLUSIVE:
      return !ids.includes(storeId);
    case LoyaltyServiceOfferStoreRestrictionMode.ALL_STORES:
    default:
      return true;
  }
}

/** INCLUSIVE with an empty allowlist means the offer is available nowhere. */
export function isInclusiveOfferUnavailableEverywhere(
  mode: OfferStoreRestrictionMode,
  availableStoreIds: readonly (string | null | undefined)[] | null | undefined
): boolean {
  if (mode !== LoyaltyServiceOfferStoreRestrictionMode.INCLUSIVE) {
    return false;
  }
  return (availableStoreIds ?? []).filter(Boolean).length === 0;
}

export function filterStoresByOfferRestriction<T extends { number?: string | null }>(
  stores: readonly T[],
  mode: OfferStoreRestrictionMode,
  availableStoreIds: readonly (string | null | undefined)[] | null | undefined
): T[] {
  if (!mode || mode === LoyaltyServiceOfferStoreRestrictionMode.ALL_STORES) {
    return [...stores];
  }

  return stores.filter(store => isStoreAllowed(store.number, mode, availableStoreIds));
}
